import { Quote, Youtube, GraduationCap, FileCode2, Wrench } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const ACTIVITIES = [
  { icon: Wrench, t: "Horsepower Garage", d: "Notre réseau de centres spécialisés dans la reprogrammation moteur, la conversion E85, le diagnostic, l'entretien et l'optimisation automobile." },
  { icon: FileCode2, t: "Horsepower Tuning Files / File Service", d: "Notre plateforme professionnelle de distribution de fichiers de cartographie moteur, portée par une équipe d'ingénieurs motoristes expérimentés et des technologies d'automatisation.", href: "https://horsepowertuningfiles.com/" },
  { icon: GraduationCap, t: "Horsepower Academy", d: "Notre projet de formation destiné à transmettre notre savoir-faire et à accompagner les futurs professionnels de la reprogrammation moteur.", href: "https://horsepower-academy.com/" },
  { icon: Youtube, t: "Horsepower TV", d: "Notre média consacré à l'automobile, aux préparations moteur, aux coulisses de nos centres et à l'entrepreneuriat.", href: "https://www.youtube.com/@HorsePowerGarage/videos" },
];

export function FounderSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="founder-section">
      <div className="grid lg:grid-cols-[440px_1fr] gap-12 lg:gap-16 items-start">
        <Reveal>
          <div className="relative">
            <div className="hidden sm:block absolute -left-3 -top-3 h-full w-full border-2 border-hp-yellow" aria-hidden />
            <img src="/photos/leo-corvitto.jpg" alt="Léo Corvitto, fondateur et dirigeant de Horsepower" className="relative w-full aspect-[2/3] object-cover object-top" data-testid="founder-photo" />
            <div className="relative bg-hp-yellow text-black p-5 -mt-px">
              <p className="font-display font-black uppercase tracking-tight text-2xl">Léo Corvitto</p>
              <p className="text-xs font-bold uppercase tracking-[0.2em] mt-1">Fondateur & dirigeant de Horsepower · 28 ans</p>
            </div>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <p className="label-eyebrow mb-3">Le fondateur</p>
            <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[0.98]">
              Une passion devenue <span className="text-hp-yellow">un groupe</span>
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="relative mt-8 pl-6 border-l-2 border-hp-yellow">
              <Quote className="absolute -left-3 -top-3 bg-hp-dark text-hp-yellow" size={22} />
              <p className="text-neutral-200 text-lg leading-relaxed font-light3">
                Passionné d'automobile, de mécanique et de performance depuis mon plus jeune âge, j'ai décidé de transformer cette passion en véritable projet entrepreneurial. J'ai créé Horsepower avec une ambition : proposer une approche professionnelle, exigeante et accessible de la reprogrammation moteur et de l'optimisation automobile.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-8 space-y-5 text-neutral-400 leading-relaxed">
              <p>Dès le départ, j'ai compris que pour construire une entreprise solide et ambitieuse, il fallait savoir s'entourer des bonnes personnes. J'ai donc choisi de travailler avec certains des <span className="text-white">meilleurs ingénieurs motoristes</span>, disposant d'une expertise approfondie dans le développement et l'optimisation des cartographies moteur, afin de garantir un haut niveau de qualité et de technicité.</p>
              <p>Pour le développement des centres physiques, j'ai fait le choix de m'associer à des <span className="text-white">amis de confiance</span>, partageant les mêmes valeurs, la même passion et surtout la même ambition entrepreneuriale. Une grande entreprise ne se construit jamais seul : la réussite de Horsepower repose autant sur les compétences techniques de nos ingénieurs que sur la confiance, l'engagement et la complémentarité des personnes qui m'accompagnent.</p>
              <p>Au fil des années, nous avons développé plusieurs centres Horsepower — Béziers, Perpignan, Montpellier, Marseille, Angers, Metz — avec l'ambition de poursuivre notre expansion à travers la France.</p>
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal delay={0.1}>
        <div className="mt-20">
          <p className="label-eyebrow mb-3">Nos activités</p>
          <h3 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl">Un groupe, quatre expertises</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {ACTIVITIES.map((a) => {
              const Tag = a.href ? "a" : "div";
              return (
                <Tag key={a.t} {...(a.href ? { href: a.href, target: "_blank", rel: "noopener noreferrer" } : {})} className="hp-card p-6 h-full border-t-2 border-hp-yellow block hover:bg-white/[0.04] transition-colors" data-testid={`activity-${a.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <a.icon className="text-hp-yellow" size={26} />
                  <p className="font-display font-bold uppercase tracking-tight text-lg mt-4">{a.t}</p>
                  <p className="text-neutral-400 text-sm mt-2 leading-relaxed">{a.d}</p>
                </Tag>
              );
            })}
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-16 hp-card p-8 sm:p-12 grid lg:grid-cols-[auto_1fr] gap-8 items-center" data-testid="founder-vision">
          <div className="font-display font-black uppercase tracking-tight text-5xl sm:text-6xl text-hp-yellow leading-none">Ma<br />vision</div>
          <div className="text-neutral-300 leading-relaxed space-y-4">
            <p>Mon objectif n'a jamais été simplement d'ouvrir des garages. Je souhaite construire un véritable <span className="text-white font-bold">groupe autour de la marque Horsepower</span>, en réunissant les meilleurs talents, des technologies innovantes et des entrepreneurs passionnés.</p>
            <p>Je veux développer une marque forte, reconnue pour son expertise, son sérieux et sa capacité à innover, aussi bien en France qu'à l'international.</p>
            <p className="text-white">Horsepower est né d'une passion personnelle, mais c'est aujourd'hui une aventure collective portée par une équipe qui partage la même ambition : <span className="text-hp-yellow font-bold">aller toujours plus loin.</span></p>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
